"use client"

import { useState } from "react"

import { Button, Tooltip } from "@coval/ui"
import { ZoomIn, ZoomOut } from "lucide-react"

import { chartBarSeriesColors } from "@/components/charts/seriesPalette"

import type { SampleCell, SampleDataTable } from "./SqlSchemaPanel"

const MIN_ZOOM = 1
const MAX_ZOOM = 6
const ZOOM_STEP = 1.5

// Inset the time axis by this much on each end so marks at 0ms / the max offset
// aren't flush against the track edges (which reads like the timeline is cut off).
const EDGE_PADDING_PERCENT = 3
const AXIS_SPAN_PERCENT = 100 - 2 * EDGE_PADDING_PERCENT

/**
 * An inline timeline of a Custom SQL metric's sample data — one swimlane per
 * data-frame table, a mark per sample row placed at its offset (a dot for a
 * point frame, a bar for an interval frame). Hovering (or focusing) a mark shows
 * that frame's JSON. A miniature of the standalone Simulation Data Frames
 * visualizer, sized to sit under the query editor. Not a card: it flows with the
 * form.
 */

// Categorical swimlane palette — the shared Coval Accessible Data Palette
// (theme-aware bar tones), so lanes read on-palette with the rest of the app.
const LANE_COLORS = chartBarSeriesColors(8)

interface Mark {
  start: number
  end: number
  /** The full sample row this mark represents, shown as JSON on hover. */
  row: Record<string, SampleCell>
}

interface Lane {
  name: string
  color: string
  marks: Mark[]
}

/** Coerce a sample cell to a finite number (JSON numbers or numeric strings). */
function toNumber(value: SampleCell | undefined): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

function buildLanes(sampleData: readonly SampleDataTable[]): Lane[] {
  const lanes: Lane[] = []
  for (const table of sampleData) {
    const marks: Mark[] = []
    for (const row of table.rows) {
      const start =
        toNumber(row.start_offset_milliseconds) ??
        toNumber(row.time_offset_milliseconds)
      if (start === undefined) {
        continue
      }
      const end = toNumber(row.end_offset_milliseconds) ?? start
      marks.push({ start, end: Math.max(end, start), row })
    }
    if (marks.length > 0) {
      lanes.push({
        name: table.name,
        color: LANE_COLORS[lanes.length % LANE_COLORS.length],
        marks,
      })
    }
  }
  return lanes
}

function formatMs(ms: number): string {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(ms >= 10000 ? 0 : 1)}s`
  }
  return `${Math.round(ms)}ms`
}

/**
 * Recursively parses string values that are themselves JSON (e.g. a tool call's
 * `arguments`/`output`) into real objects, so the hover renders nested data
 * expanded instead of as an escaped string blob.
 */
export function expandNestedJson(value: unknown): unknown {
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return expandNestedJson(JSON.parse(trimmed))
      } catch {
        return value
      }
    }
    return value
  }
  if (Array.isArray(value)) {
    return value.map(expandNestedJson)
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, inner]) => [
        key,
        expandNestedJson(inner),
      ]),
    )
  }
  return value
}

function FrameMark({
  mark,
  color,
  laneName,
  maxMs,
}: {
  mark: Mark
  color: string
  laneName: string
  maxMs: number
}) {
  const isInterval = mark.end > mark.start
  const left = `${EDGE_PADDING_PERCENT + (mark.start / maxMs) * AXIS_SPAN_PERCENT}%`
  return (
    <Tooltip
      side="top"
      className="!animate-none"
      content={
        <pre className="max-h-64 max-w-xs overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-snug">
          {JSON.stringify(expandNestedJson(mark.row), null, 2)}
        </pre>
      }
    >
      <button
        type="button"
        aria-label={`${laneName} frame at ${formatMs(mark.start)}`}
        className="absolute top-1/2 -translate-y-1/2 rounded-sm outline-none ring-offset-1 transition hover:z-10 hover:scale-125 hover:brightness-125 hover:ring-2 hover:ring-coval-info-border focus-visible:z-10 focus-visible:scale-125 focus-visible:brightness-125 focus-visible:ring-2 focus-visible:ring-coval-info-border"
        style={
          isInterval
            ? {
                left,
                width: `${((mark.end - mark.start) / maxMs) * AXIS_SPAN_PERCENT}%`,
                minWidth: 3,
                height: 12,
                backgroundColor: color,
              }
            : {
                left,
                width: 10,
                height: 10,
                marginLeft: -5,
                borderRadius: 9999,
                backgroundColor: color,
              }
        }
      />
    </Tooltip>
  )
}

export function SqlSampleTimeline({
  sampleData,
}: {
  sampleData: readonly SampleDataTable[]
}) {
  const [zoom, setZoom] = useState(1)
  const lanes = buildLanes(sampleData)
  if (lanes.length === 0) {
    return null
  }
  const maxMs = Math.max(
    1,
    ...lanes.flatMap((lane) => lane.marks.map((m) => m.end)),
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-coval-text-muted">
          Sample data timeline
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-coval-text-muted">
            hover a mark for its frame
          </span>
          <div className="flex items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              icon
              className="h-6 w-6 p-0 text-coval-text-muted"
              tooltip="Zoom out timeline"
              disabled={zoom <= MIN_ZOOM}
              onClick={() => setZoom((z) => Math.max(z / ZOOM_STEP, MIN_ZOOM))}
            >
              <ZoomOut className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              icon
              className="h-6 w-6 p-0 text-coval-text-muted"
              tooltip="Zoom in timeline"
              disabled={zoom >= MAX_ZOOM}
              onClick={() => setZoom((z) => Math.min(z * ZOOM_STEP, MAX_ZOOM))}
            >
              <ZoomIn className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        {/* Fixed lane labels — stay put while the tracks scroll when zoomed. */}
        <div className="flex shrink-0 flex-col gap-1.5">
          {lanes.map((lane) => (
            <div
              key={lane.name}
              className="flex h-7 w-40 items-center truncate font-mono text-xs text-coval-text-muted"
              title={lane.name}
            >
              {lane.name}
            </div>
          ))}
        </div>

        {/* Zoomable, horizontally scrollable track area. */}
        <div className="min-w-0 flex-1 overflow-x-auto">
          <div
            className="flex flex-col gap-1.5"
            style={{ width: `${zoom * 100}%` }}
          >
            {lanes.map((lane) => (
              <div
                key={lane.name}
                className="relative h-7 rounded bg-surface-2"
              >
                {lane.marks.map((mark, index) => (
                  <FrameMark
                    key={index}
                    mark={mark}
                    color={lane.color}
                    laneName={lane.name}
                    maxMs={maxMs}
                  />
                ))}
              </div>
            ))}
            <div
              className="flex justify-between text-[11px] text-coval-text-muted"
              style={{
                paddingLeft: `${EDGE_PADDING_PERCENT}%`,
                paddingRight: `${EDGE_PADDING_PERCENT}%`,
              }}
            >
              <span>{maxMs >= 1000 ? "0s" : "0ms"}</span>
              <span>{formatMs(maxMs)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
